/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './Button';
import { attributesApi, categoriesApi } from '../../services/api';

interface TestResult {
  name: string;
  status: 'pending' | 'success' | 'error';
  message?: string;
  data?: any;
  error?: any;
  url?: string;
  duration?: number;
}

export const ApiTest: React.FC = () => {
  const [tests, setTests] = useState<TestResult[]>([
    { name: 'Inventory API Health', status: 'pending', url: '/api/health' },
    { name: 'AI API Health', status: 'pending', url: '/api/health' },
    { name: 'Attributes API', status: 'pending', url: '/attributes' },
    { name: 'Categories API', status: 'pending', url: '/categories' },
  ]);
  const [testing, setTesting] = useState(false);

  const updateTest = (name: string, status: 'success' | 'error', message?: string, data?: any, error?: any, duration?: number) => {
    setTests(prev => prev.map(test =>
      test.name === name ? { ...test, status, message, data, error, duration } : test
    ));
  };

  const runTests = async () => {
    setTesting(true);

    // Reset all tests to pending
    setTests(prev => prev.map(test => ({ ...test, status: 'pending' as const, message: undefined, data: undefined, error: undefined, duration: undefined })));

    // Check if API URLs are configured
    const inventoryApiUrl = import.meta.env.VITE_INVENTORY_API_URL;
    const aiApiUrl = import.meta.env.VITE_AI_API_URL;

    console.log('Inventory API URL from env:', inventoryApiUrl);
    console.log('AI API URL from env:', aiApiUrl);
    console.log('All env vars:', import.meta.env);

    if (!inventoryApiUrl && !aiApiUrl) {
      console.log('No API URLs configured, setting error states');
      updateTest('Inventory API Health', 'error', 'Inventory API URL not configured - backend not ready yet', null, { error: 'VITE_INVENTORY_API_URL is not set in .env' }, 0);
      updateTest('AI API Health', 'error', 'AI API URL not configured - backend not ready yet', null, { error: 'VITE_AI_API_URL is not set in .env' }, 0);
      updateTest('Attributes API', 'error', 'Inventory API URL not configured - backend not ready yet', null, { error: 'VITE_INVENTORY_API_URL is commented out in .env' }, 0);
      updateTest('Categories API', 'error', 'Inventory API URL not configured - backend not ready yet', null, { error: 'VITE_INVENTORY_API_URL is commented out in .env' }, 0);
      setTesting(false);
      return;
    }

    console.log('API URLs configured, running tests...');

    // Test Inventory API Health
    if (inventoryApiUrl) {
      const startTime = Date.now();
      try {
        const response = await fetch(`${inventoryApiUrl}/api/health`);
        const duration = Date.now() - startTime;
        if (response.ok) {
          const data = await response.json();
          updateTest('Inventory API Health', 'success', `Inventory server is healthy (${response.status})`, data, null, duration);
        } else {
          updateTest('Inventory API Health', 'error', `Inventory server returned ${response.status}`, null, { error: `HTTP ${response.status}` }, duration);
        }
      } catch (error) {
        const duration = Date.now() - startTime;
        updateTest('Inventory API Health', 'error', 'Failed to connect to inventory server', null, { error: error instanceof Error ? error.message : 'Unknown error' }, duration);
      }
    } else {
      updateTest('Inventory API Health', 'error', 'Inventory API URL not configured', null, { error: 'VITE_INVENTORY_API_URL is not set' }, 0);
    }

    // Test AI API Health
    if (aiApiUrl) {
      const startTime = Date.now();
      try {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        // Add ngrok header if using ngrok
        if (aiApiUrl.includes('ngrok')) {
          headers['ngrok-skip-browser-warning'] = 'true';
        }

        const response = await fetch(`${aiApiUrl}/api/health`, { headers });
        const duration = Date.now() - startTime;
        if (response.ok) {
          const data = await response.json();
          updateTest('AI API Health', 'success', `AI server is healthy (${response.status})`, data, null, duration);
        } else {
          updateTest('AI API Health', 'error', `AI server returned ${response.status}`, null, { error: `HTTP ${response.status}` }, duration);
        }
      } catch (error) {
        const duration = Date.now() - startTime;
        updateTest('AI API Health', 'error', 'Failed to connect to AI server', null, { error: error instanceof Error ? error.message : 'Unknown error' }, duration);
      }
    } else {
      updateTest('AI API Health', 'error', 'AI API URL not configured', null, { error: 'VITE_AI_API_URL is not set' }, 0);
    }

    // Test Inventory API endpoints (only if inventory API is configured)
    if (inventoryApiUrl) {
      // Test Attributes API
      const attributesStartTime = Date.now();
      try {
        const response = await attributesApi.getAll();
        const attributesDuration = Date.now() - attributesStartTime;
        if (response.success && response.data) {
          updateTest('Attributes API', 'success', `Found ${response.data.length} attributes`, response.data, null, attributesDuration);
        } else {
          updateTest('Attributes API', 'error', response.message || 'Attributes API failed', null, { error: response.message }, attributesDuration);
        }
      } catch (error) {
        const attributesDuration = Date.now() - attributesStartTime;
        updateTest('Attributes API', 'error', 'Failed to connect to attributes API', null, { error: error instanceof Error ? error.message : 'Unknown error' }, attributesDuration);
      }

      // Test Categories API
      const categoriesStartTime = Date.now();
      try {
        const response = await categoriesApi.getAll();
        const categoriesDuration = Date.now() - categoriesStartTime;
        if (response.success && response.data) {
          updateTest('Categories API', 'success', `Found ${response.data.length} categories`, response.data, null, categoriesDuration);
        } else {
          updateTest('Categories API', 'error', response.message || 'Categories API failed', null, { error: response.message }, categoriesDuration);
        }
      } catch (error) {
        const categoriesDuration = Date.now() - categoriesStartTime;
        updateTest('Categories API', 'error', 'Failed to connect to categories API', null, { error: error instanceof Error ? error.message : 'Unknown error' }, categoriesDuration);
      }
    } else {
      updateTest('Attributes API', 'error', 'Inventory API URL not configured', null, { error: 'VITE_INVENTORY_API_URL is not set' }, 0);
      updateTest('Categories API', 'error', 'Inventory API URL not configured', null, { error: 'VITE_INVENTORY_API_URL is not set' }, 0);
    }

    setTesting(false);
  };

  // Test individual endpoint
  const testEndpoint = async (url: string) => {
    // Always use relative URLs - Caddy proxy routes to backend
    const fullUrl = url;
    try {
      console.log(`Testing direct fetch to: ${fullUrl}`);
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log(`Response status: ${response.status}`);
      console.log(`Response headers:`, Object.fromEntries(response.headers.entries()));

      const text = await response.text();
      console.log(`Response body:`, text);

      try {
        const json = JSON.parse(text);
        console.log(`Parsed JSON:`, json);
      } catch {
        console.log('Response is not valid JSON');
      }
    } catch (error) {
      console.error(`Direct fetch error:`, error);
    }
  };

  useEffect(() => {
    runTests();
  }, []);

  const getStatusIcon = (status: TestResult['status']) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500 animate-spin" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
    }
  };

  const getStatusColor = (status: TestResult['status']) => {
    switch (status) {
      case 'pending':
        return 'border-yellow-200 bg-yellow-50';
      case 'success':
        return 'border-green-200 bg-green-50';
      case 'error':
        return 'border-red-200 bg-red-50';
    }
  };

  return (
    <div className="space-y-6">
      {/* Main Test Results */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-navy-900">API Connection Test</h3>
            <p className="text-navy-600">Testing connectivity to your local server</p>
          </div>
          <Button
            onClick={runTests}
            loading={testing}
            variant="outline"
            size="sm"
            icon={RefreshCw}
          >
            {testing ? 'Testing...' : 'Retest'}
          </Button>
        </div>

        <div className="space-y-4">
          {tests.map((test) => (
            <div
              key={test.name}
              className={`border rounded-lg p-4 transition-colors ${getStatusColor(test.status)}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(test.status)}
                  <div>
                    <span className="font-medium text-navy-900">{test.name}</span>
                    <div className="text-xs text-navy-500">
                      {test.url} {test.duration && `(${test.duration}ms)`}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`text-sm font-medium ${test.status === 'success' ? 'text-green-700' :
                      test.status === 'error' ? 'text-red-700' : 'text-yellow-700'
                    }`}>
                    {test.status.toUpperCase()}
                  </span>
                  {test.status === 'error' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => testEndpoint(test.url!)}
                      className="text-xs"
                    >
                      Debug
                    </Button>
                  )}
                </div>
              </div>

              {test.message && (
                <p className={`text-sm mb-2 ${test.status === 'success' ? 'text-green-700' :
                    test.status === 'error' ? 'text-red-700' : 'text-yellow-700'
                  }`}>
                  {test.message}
                </p>
              )}

              {test.error && (
                <details className="mt-2">
                  <summary className="text-sm text-red-600 cursor-pointer hover:text-red-800">
                    View Error Details
                  </summary>
                  <pre className="mt-2 text-xs bg-red-50 p-2 rounded overflow-x-auto text-red-800">
                    {JSON.stringify(test.error, null, 2)}
                  </pre>
                </details>
              )}

              {test.data && test.status === 'success' && (
                <details className="mt-2">
                  <summary className="text-sm text-navy-600 cursor-pointer hover:text-navy-800">
                    View Response Data ({Array.isArray(test.data) ? test.data.length : 'object'})
                  </summary>
                  <pre className="mt-2 text-xs bg-navy-50 p-2 rounded overflow-x-auto max-h-40">
                    {JSON.stringify(test.data, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Configuration Info */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center space-x-2 mb-4">
          <AlertTriangle className="w-5 h-5 text-navy-600" />
          <span className="font-medium text-navy-900">Configuration & Troubleshooting</span>
        </div>

        <div className="space-y-3 text-sm">
          <div>
            <span className="font-medium text-navy-700">Inventory API URL:</span>
            <code className="ml-2 bg-navy-100 px-2 py-1 rounded text-xs">
              {import.meta.env.VITE_INVENTORY_API_URL || 'Not configured'}
            </code>
          </div>

          <div>
            <span className="font-medium text-navy-700">AI API URL:</span>
            <code className="ml-2 bg-navy-100 px-2 py-1 rounded text-xs">
              {import.meta.env.VITE_AI_API_URL || 'Not configured'}
            </code>
          </div>

          <div>
            <span className="font-medium text-navy-700">Expected Endpoints:</span>
            <ul className="ml-4 mt-1 space-y-1 text-xs text-navy-600">
              <li><strong>Inventory API:</strong></li>
              <li>  • GET /api/status</li>
              <li>  • GET /api/attributes</li>
              <li>  • GET /api/categories</li>
              <li>  • GET /api/products</li>
              <li><strong>AI API:</strong></li>
              <li>  • GET /api/status</li>
              <li>  • POST /api/ai/execute</li>
            </ul>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
            <span className="font-medium text-yellow-800">Common Issues:</span>
            <ul className="ml-4 mt-1 space-y-1 text-xs text-yellow-700">
              <li>• <strong>IP not whitelisted:</strong> bolt.new's IP addresses need to be allowed on your servers</li>
              <li>• <strong>CORS issues:</strong> Your FastAPI servers need to allow bolt.new origins</li>
              <li>• <strong>Firewall blocking:</strong> Ports 80/443 need to be open for bolt.new IPs</li>
              <li>• <strong>API servers down:</strong> Check if your servers are running</li>
              <li>• <strong>Wrong URLs:</strong> Verify the API endpoints are correct</li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded p-3">
            <span className="font-medium text-blue-800">Quick Fixes:</span>
            <ul className="ml-4 mt-1 space-y-1 text-xs text-blue-700">
              <li>• <strong>Step 1:</strong> Use the "IP Detection" tab to find bolt.new's IP addresses</li>
              <li>• <strong>Step 2:</strong> Whitelist those IPs on both your API servers</li>
              <li>• <strong>Step 3:</strong> Configure CORS to allow bolt.new origins</li>
              <li>• <strong>Step 4:</strong> Test the connection again on this page</li>
              <li>• <strong>Debug:</strong> Check browser console for detailed error messages</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};